import { audio } from '../systems/audio/AudioSystem.js';
import { KEYBINDS } from '../../config/constants.js';

/**
 * The Verse's card hand (Act III, systems/act3/VerseSystem.js) — the
 * LevelUpPicker inverted.
 *
 * Every other pick screen in the game asks what you want to become. This one
 * asks what you want the boss to become, and every card is bad for you; one is
 * less so. Deliberately built as its own overlay rather than reusing
 * LevelUpPicker: that class is bound to ProgressionSystem's pick lifecycle, and
 * more importantly the two must never *look* the same. A player who reflexively
 * takes the shiniest card here has misread the screen.
 *
 * Declining is a real option and a real mistake — refusal is a choice of verse
 * too, and the worst one (VerseSystem takes the card the fight most wants).
 */
export class VersePicker {
  constructor(eventBus, root) {
    this.eventBus = eventBus;
    this.root = root;
    this.el = null;
    this._cards = [];

    this._onKeyDown = (e) => {
      const index = KEYBINDS.levelUpPick.indexOf(e.code);
      if (index === -1) return;
      const card = this._cards[index];
      if (card) this._pick(card.id);
    };

    eventBus.on('verse:offer', ({ cards }) => this._show(cards));
    // The system emits this for every resolution path, including a fight that
    // ends mid-offer, so the overlay can never be orphaned on screen.
    eventBus.on('verse:picked', () => this._hide());
    eventBus.on('run:ended', () => this._hide());
  }

  _hide() {
    this.el?.remove();
    this.el = null;
    this._cards = [];
    window.removeEventListener('keydown', this._onKeyDown);
  }

  _show(cards) {
    this._hide();
    this._cards = cards;
    window.addEventListener('keydown', this._onKeyDown);

    this.el = document.createElement('div');
    this.el.className = 'levelup-overlay verse-overlay';

    const title = document.createElement('h2');
    title.className = 'levelup-title verse-title';
    title.textContent = 'The Verse offers you the pen';
    this.el.appendChild(title);

    const subtitle = document.createElement('div');
    subtitle.className = 'levelup-subtitle';
    subtitle.textContent = 'Choose how it grows. All of these are bad for you.';
    this.el.appendChild(subtitle);

    const list = document.createElement('div');
    list.className = 'levelup-cards';
    for (const card of cards) {
      const el = document.createElement('button');
      el.type = 'button';
      el.className = 'levelup-card verse-card';

      const kicker = document.createElement('div');
      kicker.className = 'levelup-card-rarity';
      kicker.textContent = card.id.startsWith('unwrite:') ? '✂ UNWRITE' : '✎ VERSE';
      el.appendChild(kicker);

      const label = document.createElement('div');
      label.className = 'levelup-card-label';
      label.textContent = card.label;
      el.appendChild(label);

      // The card names its trade honestly — that honesty is the boss's
      // characterisation, so it must not be softened in the UI.
      const trade = document.createElement('div');
      trade.className = 'levelup-card-desc levelup-card-cost';
      trade.textContent = card.trade;
      el.appendChild(trade);

      el.addEventListener('click', () => this._pick(card.id));
      list.appendChild(el);
    }
    this.el.appendChild(list);

    const decline = document.createElement('button');
    decline.type = 'button';
    decline.className = 'menu-button verse-decline';
    decline.textContent = 'Refuse the pen';
    decline.title = 'Refusal is a choice of verse too, and the worst one.';
    decline.addEventListener('click', () => this._pick(null));
    this.el.appendChild(decline);

    this.root.appendChild(this.el);
  }

  _pick(id) {
    audio.playSfx('click');
    window.removeEventListener('keydown', this._onKeyDown);
    this._cards = [];
    // The system owns the consequence and emits verse:picked, which is what
    // actually tears this down — same contract LevelUpPicker has.
    this.eventBus.emit('verse:pick', { id });
  }
}
